'use strict';
import 'leaflet/dist/leaflet.css';
import 'leaflet-control-geocoder/dist/Control.Geocoder.css';
import swal from 'sweetalert';
import L from 'leaflet';
import 'leaflet-control-geocoder';
import 'core-js/stable';
import 'regenerator-runtime/runtime';

class Workout {
  date = new Date();
  id = this._uuid();
  clicks = 0;
  constructor(coords, distance, duration) {
    this.coords = coords;
    this.distance = distance;
    this.duration = duration;
  }

  _setDescription() {
    //prettier-ignore
    const months = [
      'January', 'February', 'March',
      'April', 'May', 'June',
      'July', 'August', 'September',
      'October', 'November', 'December',
    ];

    this.description = `${this.type[0].toUpperCase()}${this.type.slice(1)} on ${
      months[this.date.getMonth()]
    } ${this.date.getDate()} at ${this.date.getHours()}:${this.date
      .getMinutes()
      .toString()
      .padStart(2, '0')}:${this.date.getSeconds().toString().padStart(2, '0')}`;
  }

  _uuid() {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
      (
        +c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
      ).toString(16)
    );
  }

  click() {
    this.clicks++;
  }
}

class Running extends Workout {
  type = 'running';
  constructor(coords, distance, duration, cadence) {
    super(coords, distance, duration);
    this.cadence = cadence;
    this.calcPace();
    this._setDescription();
  }

  calcPace() {
    this.pace = this.duration / this.distance;
    return this.pace;
  }
}

class Cycling extends Workout {
  type = 'cycling';
  constructor(coords, distance, duration, elevationGain) {
    super(coords, distance, duration);
    this.elevationGain = elevationGain;
    this.calcSpeed();
    this._setDescription();
  }

  calcSpeed() {
    this.speed = this.distance / (this.duration / 60);
    return this.speed;
  }
}

////////////////////////////
// APPLICATION ARCHITECTURE

const form = document.querySelector('.form');
const containerWorkouts = document.querySelector('.workouts');
const inputType = document.querySelector('.form__input--type');
const inputDistance = document.querySelector('.form__input--distance');
const inputDuration = document.querySelector('.form__input--duration');
const inputCadence = document.querySelector('.form__input--cadence');
const inputElevation = document.querySelector('.form__input--elevation');
const messageDiv = document.querySelector('.message');
const sortDiv = document.querySelector('.sort__row');
const inputSort = document.querySelector('.sort__input');

class App {
  #map; //stores the map element
  #mapEvent; // stores the map event
  #mapZoomLevel = 16; // stores the desired zoomlevel
  #workouts = []; // workouts array
  #new = true; // is this a new entry or an existing one?
  #currentWorkout; // existing entry - workout
  #currentElement; // existing entry - element
  #location; // locationName
  #control; // geocoder control
  #geocoder; // geocoder

  constructor() {
    // user position
    this._getPosition();

    //handlers
    form.addEventListener('submit', this._processWorkout.bind(this));

    inputType.addEventListener('change', this._toggleElevationField);

    inputSort.addEventListener('change', this._sortList.bind(this));

    containerWorkouts.addEventListener('click', this._moveToPopup.bind(this));

    // load local storage
    this._getLocalStorage();
  }

  _sortList(e) {
    const sortBy = e.target.value;
    // console.log('Will sort by ' + sortBy);
    switch (sortBy) {
      case 'desc':
        this.#workouts = this.#workouts.sort((a, b) =>
          a.description > b.description ? -1 : 1
        );
        break;
      case 'type':
        this.#workouts = this.#workouts.sort((a, b) =>
          a.type > b.type ? -1 : 1
        );
        break;
      case 'duration':
        this.#workouts = this.#workouts.sort((a, b) => a.duration - b.duration);
        break;
      case 'distance':
        this.#workouts = this.#workouts.sort((a, b) => a.distance - b.distance);
        break;
      case 'date':
        this.#workouts = this.#workouts.sort((a, b) =>
          a.date > b.date ? 1 : -1
        );
        break;
      default:
        this.#workouts = this.#workouts.sort(
          (a, b) => b.originalSort - a.originalSort
        );
        break;
    }
    // console.log('Sorted: ', this.#workouts);
    this._clearList();
    this._renderList();
  }

  _clearList() {
    let elementCount = containerWorkouts.childElementCount;
    let i = 0;
    while (elementCount > 1 && i < 50) {
      i++;
      elementCount = containerWorkouts.childElementCount;
      for (let node of containerWorkouts.children) {
        if (node.nodeName !== 'FORM') containerWorkouts.removeChild(node);
      }
    }
  }

  _renderList() {
    this.#workouts.forEach((workout) => {
      this._renderWorkout(workout);
    });
  }

  _showMessage(message, style) {
    if (style)
      style.forEach((className) => messageDiv.classList.add(className));
    messageDiv.innerHTML = message;
  }

  _clearMessage() {
    messageDiv.setAttribute('class', 'message');
    messageDiv.innerHTML = '';
  }

  _sendMessage(message, style, time) {
    this._clearMessage();
    this._showMessage(message, style);
    if (time > 0) setTimeout(this._clearMessage, time * 1000);
  }

  _getPosition() {
    if (navigator.geolocation) {
      this._sendMessage(' ... loading ...', ['blue', 'blink'], 0);
      navigator.geolocation.getCurrentPosition(
        this._loadMap.bind(this),
        function () {
          alert('Could not get your position');
        }
      );
    }
  }

  _loadMap(position) {
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    const coords = [latitude, longitude];
    // console.log('Position: ', latitude, longitude);
    this.#map = L.map('map').setView(coords, this.#mapZoomLevel);

    L.tileLayer('https://tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.#map);

    this.#geocoder = L.Control.Geocoder.nominatim();

    this.#control = L.Control.geocoder().addTo(this.#map);

    this.#map.on('click', this._showForm.bind(this));

    this.#workouts.forEach((workout) => {
      this._renderWorkoutMarker(workout);
    });

    this._sendMessage('... success ...', ['green', 'light'], 4);
  }

  _newWorkout(
    coords,
    type,
    distance,
    duration,
    cadence,
    elevation,
    description,
    date,
    order,
    location
  ) {
    // Create a new Workout with given data
    let lat, lng;
    if (this.#mapEvent) {
      // new Workout triggered by a click on the map
      // coords come from mapEvent
      ({ lat, lng } = this.#mapEvent.latlng);
    } else {
      // new Workout triggered by other situations
      // coords come from the parameters
      [lat, lng] = coords;
    }

    let workout;

    if (type === 'running') {
      workout = new Running([lat, lng], distance, duration, cadence);
    }

    if (type === 'cycling') {
      workout = new Cycling([lat, lng], distance, duration, elevation);
    }

    if (order !== 'new') {
      workout.originalSort = order;
    } else if (this.#workouts.length > 0) {
      workout.originalSort = this.#workouts.reduce((acc, w) => {
        if (+w.originalSort > acc) {
          return +w.originalSort;
        } else {
          return acc;
        }
      }, this.#workouts[0].originalSort);

      workout.originalSort++;
    } else {
      workout.originalSort = 1;
    }

    // restore location from loaded localStorage or mapEvent
    if (location !== 'new') workout.location = location;
    else if (this.#location) workout.location = this.#location;
    else workout.location = 'unidentified location';

    // restore description and date from loaded localStorage
    // console.log('This is the date I got ' + date);
    if (description !== 'new') {
      // console.log('Will restore description to ' + description);
      workout.description = description;
    }
    if (date !== 'new') {
      // console.log('Will restore date to ' + date);
      workout.date = new Date(date);
    }
    // add workout to workout array
    this.#workouts.push(workout);
    //console.log(workout);

    // render workout on map as marker
    // only if it is a new marker
    if (this.#mapEvent) this._renderWorkoutMarker(workout);

    // render workout on list
    this._renderWorkout(workout);
  }

  _removeOldWorkoutFromList() {
    //remove element from list
    this.#currentElement.remove();
    // this is the end of the tool operation, reset properties in app
    this.#currentElement = null;
    this.#currentWorkout = null;
    this.#new = true;
  }

  _editWorkoutList(workout) {
    // after editing workout data, remove element from list and render a new one
    this._renderWorkout(workout);
    this._removeOldWorkoutFromList();
  }

  _editWorkoutData(workout, type, distance, duration, other) {
    // edit workout data with new values

    workout.distance = distance;
    workout.duration = duration;
    if (type === 'running') {
      workout.cadence = other;
      workout.calcPace();
    }
    if (type === 'cycling') {
      workout.elevationGain = other;
      workout.calcSpeed();
    }
    this._editWorkoutList(workout);
    //Set local storage to all workouts
    this._setLocalStorage();
  }

  _editWorkout(workout) {
    // a workout record will be edited
    this.#new = false;
    this.#currentWorkout = workout;
    // loads the current values in the form element
    this._loadForm(workout);
  }

  _deleteWorkout(workout) {
    // console.log('Will delete', workout);
    // console.log('CE ', this.#currentElement);
    swal('Delete the workout?', {
      dangerMode: true,
      buttons: true,
    }).then((value) => {
      if (value) {
        //proceed
        this.#new = false;
        this.#currentWorkout = workout;
        this._removeOldWorkoutFromList();
        //workout.marker.remove();
        const newWorkouts = this.#workouts.filter(
          (work) => work.id !== workout.id
        );
        this.#workouts = newWorkouts;
        // console.log('Deleted ', this.#workouts);
        //Set local storage to all workouts
        this._setLocalStorage();
        //console.log(this.#map);
        this.#map.remove();
        this._getPosition();
      }
    });
  }

  _workoutTool(workout, tool) {
    // a tool was selected, either delete or edit
    if (tool.classList.contains('workout__edit')) this._editWorkout(workout);
    if (tool.classList.contains('workout__delete'))
      this._deleteWorkout(workout);
  }

  _moveToPopup(e) {
    // a card was clicked in the list
    const workoutEl = e.target.closest('.workout');
    if (!workoutEl) return;
    const workout = this.#workouts.find(
      (work) => work.id === workoutEl.dataset.id
    );
    this.#map.setView(workout.coords, this.#mapZoomLevel, {
      animate: true,
      pan: { duration: 1 },
    });
    workout.click();

    // if a tool was clicked, process the tool
    const workoutTool = e.target.closest('.workout__tool--icon');

    if (workoutTool) {
      this.#currentElement = workoutEl;
      this._workoutTool(workout, workoutTool);
    }

    //console.log('Check clicks:', workout);
  }

  _loadForm(workout) {
    this._sendMessage('... editing workout ...', ['yellow', 'light', 'blink']);
    form.classList.remove('hidden');
    if (inputType.value !== workout.type) this._toggleElevationField();
    inputType.value = workout.type;
    inputDistance.value = workout.distance;
    inputDuration.value = workout.duration;
    inputType.disabled = true;
    if (workout.type === 'running') inputCadence.value = workout.cadence;
    if (workout.type === 'cycling')
      inputElevation.value = workout.elevationGain;
    inputDistance.focus();
  }

  _findLocation(e) {
    this.#geocoder.reverse(
      e.latlng,
      this.#map.options.crs.scale(this.#map.getZoom()),
      function (results) {
        // console.log(results);
        var r = results[0];
        // console.log(r);
        if (r) {
          // console.log(r.properties.address.suburb);
          // console.log(r.properties.address.city_district);
          // console.log(r.properties.address.city);
          if (!r.properties.address.suburb)
            this.#location =
              r.properties.address.city_district +
              ', ' +
              r.properties.address.city;
          else
            this.#location =
              r.properties.address.suburb + ', ' + r.properties.address.city;
          // console.log(this.#location);
        } else this.#location = 'unidentified location';
      }.bind(this)
    );
  }

  _showForm(mapE) {
    this._sendMessage('... new workout ...', ['green', 'light', 'blink']);
    this.#mapEvent = mapE;
    this._findLocation(mapE);
    form.classList.remove('hidden');
    inputType.disabled = false;
    inputDistance.focus();
  }

  _hideForm() {
    //avoid "hidden" animation by seting display to none, then back to grid after one second
    form.style.display = 'none';
    form.classList.add('hidden');
    setTimeout(() => (form.style.display = 'grid'), 1000);
  }

  _resetForm(type) {
    inputDistance.value =
      inputDuration.value =
      inputCadence.value =
      inputElevation.value =
        '';
    if (type === 'cycling') {
      inputType.value = 'running';
      this._toggleElevationField();
    }
    this._hideForm();
  }

  _toggleElevationField() {
    inputElevation.closest('.form__row').classList.toggle('form__row--hidden');
    inputCadence.closest('.form__row').classList.toggle('form__row--hidden');
  }

  _processWorkout(e) {
    const validInputs = (...inputs) =>
      inputs.every((inp) => Number.isFinite(inp));
    const allPositive = (...inputs) => inputs.every((inp) => inp > 0);

    e.preventDefault();

    // Get Data from Form
    const type = inputType.value;
    const distance = +inputDistance.value;
    const duration = +inputDuration.value;
    // console.log(type, duration, distance);

    // if activity is running, create running
    if (type == 'running') {
      const cadence = +inputCadence.value;
      // Check if data is valid
      if (
        !validInputs(distance, duration, cadence) ||
        !allPositive(distance, duration, cadence)
      ) {
        this._sendMessage(
          'Distance, duration and cadence need to be positive numbers',
          ['red', 'regular']
        );
        return;
      }

      if (this.#new) {
        this._newWorkout(
          'new',
          type,
          distance,
          duration,
          cadence,
          _,
          'new',
          'new',
          'new',
          'new'
        );
      }
      if (!this.#new) {
        this._editWorkoutData(
          this.#currentWorkout,
          type,
          distance,
          duration,
          cadence
        );
      }
    }

    // if workout is cycling, create cycling
    if (type == 'cycling') {
      const elevation = +inputElevation.value;
      if (
        !validInputs(distance, duration, elevation) ||
        !allPositive(distance, duration)
      ) {
        this._sendMessage('Distance and duration need to be positive numbers', [
          'red',
          'regular',
        ]);
        return;
      }
      if (this.#new) {
        this._newWorkout(
          'new',
          type,
          distance,
          duration,
          _,
          elevation,
          'new',
          'new',
          'new',
          'new'
        );
      }
      if (!this.#new) {
        this._editWorkoutData(
          this.#currentWorkout,
          type,
          distance,
          duration,
          elevation
        );
      }
    }

    // success message for 4 seconds
    this._sendMessage('... success ...', ['green', 'light'], 4);

    // hide form + clear inout fields
    this._resetForm(type);

    //Set local storage to all workouts
    this._setLocalStorage();
  }

  _renderWorkoutMarker(workout) {
    console.log(L.Icon.Default);
    console.log(L.Icon);
    const mark = L.marker(workout.coords)
      .addTo(this.#map)
      .bindPopup(
        L.popup({
          maxWidth: 250,
          minWidth: 100,
          autoClose: false,
          closeOnClick: false,
          className: `${workout.type}-popup`,
        })
      )
      .setPopupContent(
        `${workout.type === 'running' ? ' 🏃‍♂️ ' : ' 🚴‍♀️ '} ${workout.description}`
      )
      .openPopup();
    //console.log(mark);
  }

  _renderWorkout(workout) {
    let html = `
      <li class="workout workout--${workout.type}" data-id="${workout.id}">
        <h2 class="workout__title">${workout.description}</h2>
        <div class="workout__tools">
        <span class="workout__tool workout__edit workout__tool--icon">✏️</span>
        <span class="workout__tool workout__delete workout__tool--icon">❌</span>
        </div>
        <div class="workout__location">in ${workout.location}</div>
        <div class="workout__details">
          <span class="workout__icon">${
            workout.type === 'running' ? ' 🏃‍♂️ ' : ' 🚴‍♀️ '
          }</span>
          <span class="workout__value">${workout.distance}</span>
          <span class="workout__unit">km</span>
        </div>
        <div class="workout__details">
          <span class="workout__icon">⏱</span>
          <span class="workout__value">${workout.duration}</span>
          <span class="workout__unit">min</span>
        </div>
      `;

    if (workout.type === 'running')
      html += `
          <div class="workout__details">
            <span class="workout__icon">⚡️</span>
            <span class="workout__value">${workout.pace.toFixed(1)}</span>
            <span class="workout__unit">min/km</span>
          </div>
          <div class="workout__details">
            <span class="workout__icon">🦶🏼</span>
            <span class="workout__value">${workout.cadence}</span>
            <span class="workout__unit">spm</span>
          </div>
        </li>
    `;

    if (workout.type === 'cycling')
      html += `
          <div class="workout__details">
            <span class="workout__icon">⚡️</span>
            <span class="workout__value">${workout.speed.toFixed(1)}</span>
            <span class="workout__unit">km/h</span>
          </div>
          <div class="workout__details">
            <span class="workout__icon">⛰</span>
            <span class="workout__value">${workout.elevationGain}</span>
            <span class="workout__unit">m</span>
          </div>
        </li>
    `;

    form.insertAdjacentHTML('afterend', html);
    if (this.#workouts.length > 1)
      sortDiv.classList.remove('sort__row--hidden');
  }

  _setLocalStorage() {
    this.#workouts = this.#workouts.sort(
      (a, b) => b.originalSort - a.originalSort
    );
    localStorage.setItem('workouts', JSON.stringify(this.#workouts));
  }

  _getLocalStorage() {
    const data = JSON.parse(localStorage.getItem('workouts'));
    if (!data) return;
    //console.log(data);
    this._loadWorkouts(data);
  }

  _loadWorkouts(data) {
    //this.#workouts = data;
    data.forEach((workoutData, index) => {
      if (!workoutData.originalSort) workoutData.originalSort = index + 1;
      if (!workoutData.location) workoutData.location = 'unidentified location';
      // console.log('Date ' + workoutData.date);
      workoutData.type === 'running'
        ? this._newWorkout(
            workoutData.coords,
            workoutData.type,
            workoutData.distance,
            workoutData.duration,
            workoutData.cadence,
            null,
            workoutData.description,
            workoutData.date,
            workoutData.originalSort,
            workoutData.location
          )
        : this._newWorkout(
            workoutData.coords,
            workoutData.type,
            workoutData.distance,
            workoutData.duration,
            null,
            workoutData.elevationGain,
            workoutData.description,
            workoutData.date,
            workoutData.originalSort,
            workoutData.location
          );
    });
    this.#workouts = this.#workouts.sort(
      (a, b) => b.originalSort - a.originalSort
    );

    // this.#workouts.forEach((workout) => this._renderWorkout(workout));
  }

  reset() {
    localStorage.removeItem('workouts');
    location.reload();
  }
}

const app = new App();
